import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Select, Form } from "antd";
import { useIntl, defineMessages, FormattedMessage } from "react-intl";
import styled from "styled-components";

import { required } from "../../../lib/utils/formValidation";
import parseCloudId from "../../../lib/utils/parseCloudId";
import themeColor from "../../../lib/style/theme";
import { DIRECT_ENGINE_OUTPUTS_PREFIX } from "../../../lib/utils/constants";

const { Option } = Select;

const { Item } = Form;

const translations = defineMessages({
  fieldRequired: {
    id: "CloudChannelListV5.fieldRequired",
    defaultMessage: "Please select cloud channel ID",
  },
  sameAsInputCloudId: {
    id: "CloudChannelListV5.sameAsInputCloudId",
    defaultMessage: "Cannot be the same as input stream cloud ID",
  },
  sameAsOutputCloudId: {
    id: "CloudChannelListV5.sameAsOutputCloudId",
    defaultMessage: "Cannot be the same as output stream cloud ID",
  },
});

const CloudChannelListV5 = ({
  disabled,
  getFieldValue,
  name,
  label,
  initialValue,
  channelList,
  sameAsValidation,
  setFieldsValue,
  isTranscodingOutput,
  isDirectOutput,
  isInput,
}) => {
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (initialValue?.permissionId) {
      setFieldsValue({ [name]: { cloudId: initialValue?.permissionId } });
    }
  }, [initialValue, setFieldsValue, name]);

  const handleChange = (selectedValue, option) => {
    const selectedChannelData =
      channelList.find((channel) => +channel.permissionId === +option.key) ||
      channelList.find((channel) => +channel.cloudId === +option.key);

    if (isTranscodingOutput) {
      const outputs = getFieldValue([DIRECT_ENGINE_OUTPUTS_PREFIX]);
      outputs[name[1]].urlDestinations[name[3]] = {
        ...outputs[name[1]].urlDestinations[name[3]],
        cloudName: selectedChannelData.channelName,
      };

      setFieldsValue({ outputs });
    }

    if (isDirectOutput) {
      const outputs = getFieldValue([DIRECT_ENGINE_OUTPUTS_PREFIX]);
      const outputFormData = getFieldValue(name);
      outputs[name[1]] = { ...outputFormData, cloudName: selectedChannelData.channelName };

      setFieldsValue({ outputs });
    }

    // Engine transcoding input
    if (name[0] === "mainInput") {
      setFieldsValue({ mainInput: { inputStreamCfg: { permissionId: selectedValue } } });
      setFieldsValue({ mainInput: { inputStreamCfg: { cloudId: selectedChannelData.cloudId } } });
      setFieldsValue({ mainInput: { inputStreamCfg: { description: selectedChannelData.description } } });
      setFieldsValue({ mainInput: { inputStreamCfg: { cloudName: selectedChannelData.channelName } } });
    }

    // Engine Direct input
    if (name[0] === "input") {
      setFieldsValue({ input: { mainConnection: { permissionId: selectedValue } } });
      setFieldsValue({ input: { mainConnection: { cloudId: selectedChannelData.cloudId } } });
      setFieldsValue({ input: { mainConnection: { description: selectedChannelData.description } } });
      setFieldsValue({ input: { mainConnection: { cloudName: selectedChannelData.channelName } } });
    }
  };

  const validateCloudId = (rule, value) => {
    const inputCloudId = getFieldValue(["input", "mainConnection", "cloudId"]);
    const outputCloudId = getFieldValue(["nonMuxedOutputs", 0, "cloudId"]);

    if (!value) {
      return Promise.reject(formatMessage(translations.fieldRequired));
    }
    if (sameAsValidation === "output" && value === outputCloudId) {
      return Promise.reject(formatMessage(translations.sameAsOutputCloudId));
    }
    if (sameAsValidation === "input" && value === inputCloudId) {
      return Promise.reject(formatMessage(translations.sameAsInputCloudId));
    }

    return Promise.resolve();
  };

  const checkIfDisable = ({ cwid, cloudId, permissionId }) => {
    if (initialValue?.cloudId) {
      if (cloudId === initialValue?.cloudId) {
        return false;
      }
    }

    if (initialValue?.permissionId) {
      if (permissionId === initialValue?.permissionId) {
        return false;
      }
    }

    return cwid;
  };

  return (
    <>
      <Item
        name={[...name, ...(isInput ? ["permissionId"] : ["cloudId"])]}
        label={label}
        rules={[{ validator: validateCloudId }, { ...required }]}
      >
        <Select onSelect={handleChange} disabled={disabled}>
          {channelList.map((channel) => (
            <Option
              key={channel.permissionId || channel.cloudId}
              value={channel.permissionId || channel.cloudId}
              disabled={checkIfDisable({
                cwid: !!channel.cwid,
                permissionId: channel.permissionId,
                cloudId: channel.cloudId,
              })}
            >
              {channel.cloudId && `${parseCloudId(channel.cloudId)} `}
              {channel.description ? <span>{`${channel.description}`}</span> : <span>{`${channel.channelName}`}</span>}
              {channel.cnn && (
                <StyledSpan>
                  <>
                    {" "}
                    (<FormattedMessage id="CloudChannelListV5.usedOn" defaultMessage="Used on" /> {channel.cnn})
                  </>
                </StyledSpan>
              )}
            </Option>
          ))}
        </Select>
      </Item>
    </>
  );
};

CloudChannelListV5.propTypes = {
  disabled: PropTypes.bool,
  setFieldsValue: PropTypes.func.isRequired,
  getFieldValue: PropTypes.func.isRequired,
  initialValue: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.shape({
      cloudId: PropTypes.number.isRequired,
      channelName: PropTypes.string.isRequired,
    }),
  ]),
  label: PropTypes.string.isRequired,
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]).isRequired,
  channelList: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        cloudId: PropTypes.number.isRequired,
        channelName: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        isEncrypted: PropTypes.bool.isRequired,
        public: PropTypes.bool.isRequired,
        userId: PropTypes.string.isRequired,
      })
    ),
    PropTypes.array,
  ]).isRequired,
  sameAsValidation: PropTypes.string.isRequired,
  isDirectOutput: PropTypes.bool,
  isTranscodingOutput: PropTypes.bool,
  isInput: PropTypes.bool,
};

CloudChannelListV5.defaultProps = {
  disabled: null,
  initialValue: null,
  isDirectOutput: null,
  isTranscodingOutput: null,
  isInput: false,
};

const StyledSpan = styled.span`
  color: ${themeColor.orange};
`;

export default CloudChannelListV5;
